import { useCallback, useEffect, useRef } from "react";
import type { VehicleSessionSnapshot } from "../../interface/auth_types";
import type SocketMqttBridge from "../../interface/socket_mqtt_bridge";
import useAuthenticatedCredentials from "../../store/hook/useAuthenticatedCredentials";
import useRemoteClientState from "../../store/hook/useRemoteClientState";
import { logRemoteClientDebug } from "../../utils/remoteClient/remoteClientLogger";
import { clearStoredSession } from "../auth/session_storage";
import { clearPendingRemoteAudioStream } from "./remote_audio_registry";

function areActiveTransportsEqual(
  prev: VehicleSessionSnapshot["activeTransports"],
  next: VehicleSessionSnapshot["activeTransports"],
): boolean {
  return (
    prev.length === next.length &&
    prev.every((transport, index) => transport === next[index])
  );
}

function areVehicleSessionSnapshotsEqual(
  prev: VehicleSessionSnapshot | null,
  next: VehicleSessionSnapshot | null,
): boolean {
  if (prev === next) {
    return true;
  }

  if (!prev || !next) {
    return false;
  }

  return (
    prev.vehicleId === next.vehicleId &&
    prev.sessionState === next.sessionState &&
    prev.mqttTransportState === next.mqttTransportState &&
    prev.signalingTransportState === next.signalingTransportState &&
    prev.remoteVideoReady === next.remoteVideoReady &&
    prev.mqttError === next.mqttError &&
    prev.signalingError === next.signalingError &&
    areActiveTransportsEqual(prev.activeTransports, next.activeTransports)
  );
}

export default function useVehicleSessionGate(bridge: SocketMqttBridge | null) {
  const {
    vehicleSessionSnapshot,
    setVehicleSessionSnapshot,
    isDisconnectingVehicleSession,
    setIsDisconnectingVehicleSession,
    resetRemoteClientState,
  } = useRemoteClientState();
  const { authenticatedCredentials, clearAuthenticatedCredentials } =
    useAuthenticatedCredentials();
  const isDisconnectingVehicleSessionRef = useRef(false);
  const latestVehicleSessionSnapshotRef =
    useRef<VehicleSessionSnapshot | null>(vehicleSessionSnapshot);
  const vehicleSessionState = vehicleSessionSnapshot?.sessionState ?? "idle";
  const isVehicleSessionActive =
    vehicleSessionState === "connected" || vehicleSessionState === "partial";
  // 재연결 중에는 차량 선택 화면으로 되돌아가지 않는다(요청).
  // 전송이 잠깐 끊기면 sessionState 가 error/idle 로 떨어지는데, 예전엔 그 즉시 콕핏이
  // 언마운트되어 선택 화면으로 튕겼다. vehicleId 가 남아 있으면 "이 차량 세션을 유지하려는
  // 의도"로 보고 콕핏 화면을 유지한 채 그 자리에서 복구를 기다린다.
  // (복구가 최종 실패하면 브리지가 세션을 비우고 → vehicleId 가 사라져 선택 화면으로 복귀.)
  const isRecoveringSession =
    Boolean(vehicleSessionSnapshot?.vehicleId) &&
    Boolean(vehicleSessionSnapshot?.recovering);
  const showLoginScreen =
    !authenticatedCredentials || (!isVehicleSessionActive && !isRecoveringSession);

  useEffect(() => {
    latestVehicleSessionSnapshotRef.current = vehicleSessionSnapshot;
  }, [vehicleSessionSnapshot]);

  const setVehicleSessionSnapshotIfChanged = useCallback(
    (snapshot: VehicleSessionSnapshot) => {
      if (
        areVehicleSessionSnapshotsEqual(
          latestVehicleSessionSnapshotRef.current,
          snapshot,
        )
      ) {
        return;
      }

      latestVehicleSessionSnapshotRef.current = snapshot;
      setVehicleSessionSnapshot(snapshot);
    },
    [setVehicleSessionSnapshot],
  );

  useEffect(() => {
    const initialSnapshot = bridge?.getVehicleSessionSnapshot() ?? null;
    latestVehicleSessionSnapshotRef.current = initialSnapshot;
    resetRemoteClientState(initialSnapshot);
  }, [bridge, resetRemoteClientState]);

  useEffect(() => {
    if (!bridge) {
      return;
    }

    return bridge.subscribeVehicleSession((snapshot) => {
      logRemoteClientDebug("Vehicle session snapshot", {
        vehicleId: snapshot.vehicleId,
        sessionState: snapshot.sessionState,
        mqttTransportState: snapshot.mqttTransportState,
        signalingTransportState: snapshot.signalingTransportState,
        remoteVideoReady: snapshot.remoteVideoReady,
        activeTransports: snapshot.activeTransports,
        mqttError: snapshot.mqttError,
        signalingError: snapshot.signalingError,
        loginVisibleBecauseSessionInactive:
          snapshot.sessionState !== "connected" &&
          snapshot.sessionState !== "partial",
      });
      setVehicleSessionSnapshotIfChanged(snapshot);
    });
  }, [bridge, setVehicleSessionSnapshotIfChanged]);

  // 콕핏 "나가기" = 완전 로그아웃(요청). 단순히 차량 세션만 끊고 플릿 목록으로 돌아가는 게
  // 아니라, 로그인 화면의 로그아웃 버튼(login.tsx onClickLogoutButton)과 동일하게:
  //   1) 차량 세션 해제 → 2) 시그널링/MQTT 연결 종료 → 3) 저장 세션/인증 자격증명 삭제 →
  //   4) 수신 오디오 보관 스트림 정리. 자격증명이 비워지면 showLoginScreen=true 가 되어
  //   자격증명 로그인 화면부터 다시 시작 = "로그인한 것과 동일한 동작".
  const finalizeFullLogout = useCallback(() => {
    if (bridge) {
      bridge.disconnectSignaling();
      bridge.disconnectMqtt();
    }
    clearStoredSession();
    clearPendingRemoteAudioStream();
    // 다음 세션을 위해 #remote-audio 의 이전 스트림을 비운다(스테일 오디오 방지).
    const audioEl = document.getElementById(
      "remote-audio",
    ) as HTMLAudioElement | null;
    if (audioEl) {
      try {
        audioEl.pause();
      } catch {
        /* ignore */
      }
      audioEl.srcObject = null;
    }
    clearAuthenticatedCredentials();
  }, [bridge, clearAuthenticatedCredentials]);

  const disconnectVehicleSession = useCallback(() => {
    if (!bridge || isDisconnectingVehicleSessionRef.current) {
      return;
    }

    isDisconnectingVehicleSessionRef.current = true;
    setIsDisconnectingVehicleSession(true);
    void bridge
      .disconnectVehicleSession()
      .catch((err) => {
        console.error("[AdRemoteClient] Vehicle session disconnect failed:", err);
      })
      .finally(() => {
        finalizeFullLogout();
        isDisconnectingVehicleSessionRef.current = false;
        setIsDisconnectingVehicleSession(false);
      });
  }, [bridge, finalizeFullLogout, setIsDisconnectingVehicleSession]);

  useEffect(() => {
    logRemoteClientDebug("Login visibility", {
      showLoginScreen,
      authenticated: Boolean(authenticatedCredentials),
      vehicleSessionState,
      isVehicleSessionActive,
    });
  }, [
    authenticatedCredentials,
    isVehicleSessionActive,
    showLoginScreen,
    vehicleSessionState,
  ]);

  return {
    isDisconnectingVehicleSession,
    showLoginScreen,
    disconnectVehicleSession,
  };
}
